import { type BottomSheetModalProps } from '@gorhom/bottom-sheet'
import { AnimatedLegendList, type AnimatedLegendListProps } from '@legendapp/list/reanimated'
import React, { useImperativeHandle, useMemo, useState } from 'react'
import { Pressable, View } from 'react-native'
import { SectionTitle } from '~/components/layout/Sections'
import { LoadingView } from '~/components/LoadingView'
import { useScrollView, useStickyFooter } from '~/components/ScrollView'
import { Label } from '~/components/typography/Content'
import BottomSheet from '~/components/ui/bottom-sheet'
import { cn } from '~/lib/utils'
import Icon from '../Icon'

export type BottomSheetSelectRenderContext = 'trigger' | 'option'

export type BottomSheetSelectRenderParams<T> = {
  item: T
  selected: boolean
  context: BottomSheetSelectRenderContext
  onSelect: () => void
  itemsCount: number
  selectedItemsCount: number
}

export type BottomSheetSelectRefHandle = {
  open: () => void
  close: () => void
}

export type BottomSheetSelectProps<T> = {
  data: T[]
  ref?: React.RefObject<BottomSheetSelectRefHandle | null>
  modalKey: string
  keyExtractor: (item: T) => string
  renderItem: (params: BottomSheetSelectRenderParams<T>) => React.ReactElement
  value?: string
  onChange?: (item: T) => void
  multiple?: boolean
  values?: string[]
  onChangeValues?: (keys: string[]) => void
  label: React.ReactNode
  disabled?: boolean
  isLoading?: boolean
  estimatedItemSize?: number
  sheetTitle?: string
  className?: string
  sheetClassName?: string
  listClassName?: string
  indicatorClassName?: string
  showHandle?: boolean
  showCloseButton?: boolean
  footer?: React.ReactNode | ((props: { close: () => void; open: () => void }) => React.ReactNode)
  stackBehavior?: BottomSheetModalProps['stackBehavior']
  onEndReached?: () => void
  onEndReachedThreshold?: number
  isFetchingNextPage?: boolean
} & Omit<AnimatedLegendListProps<T>, 'data' | 'renderItem'>

export function BottomSheetSelect<T>(props: BottomSheetSelectProps<T>) {
  const {
    data,
    modalKey,
    keyExtractor,
    renderItem,
    value,
    onChange,
    label,
    disabled,
    isLoading,
    sheetTitle,
    className,
    sheetClassName,
    listClassName,
    indicatorClassName,
    footer,
    ref,
    getItemType,
    stackBehavior,
    onEndReached,
    onEndReachedThreshold,
    isFetchingNextPage,
    ...listProps
  } = props as BottomSheetSelectProps<T>

  const [isOpen, setOpen] = useState(false)
  const isMultiple = !!props.multiple

  useImperativeHandle(ref, () => ({
    open: () => setOpen(true),
    close: () => setOpen(false),
  }))

  const selectedItem = useMemo(() => {
    if (isMultiple) {
      const keys = props.values || []

      if (!keys.length) {
        return undefined
      }

      const firstKey = keys[0]
      return data?.find((it) => keyExtractor(it) === firstKey)
    }

    if (!value) {
      return undefined
    }

    return data?.find((it) => keyExtractor(it) === value)
  }, [data, isMultiple, keyExtractor, props.values, value])

  const selectedKey = useMemo(() => {
    if (isMultiple) {
      return undefined
    }

    return selectedItem ? keyExtractor(selectedItem) : undefined
  }, [isMultiple, keyExtractor, selectedItem])

  const { props: scrollProps, scrollIndicator } = useScrollView({
    indicatorClassName: cn('right-0', indicatorClassName),
  })

  const {
    element: footerElement,
    scrollViewProps: scrollViewFooterProps,
    footerHeight,
  } = useStickyFooter({
    content:
      typeof footer === 'function'
        ? footer({ close: () => setOpen(false), open: () => setOpen(true) })
        : footer,
  })

  const extraData = useMemo(
    () => ({
      selectedKeys: isMultiple
        ? props.values || []
        : selectedItem
          ? [keyExtractor(selectedItem)]
          : [],
      isMultiple,
    }),
    [isMultiple, props.values, selectedItem, keyExtractor]
  )

  const triggerContent = useMemo(() => {
    if (selectedItem) {
      return renderItem({
        item: selectedItem,
        selected: true,
        context: 'trigger',
        onSelect: () => setOpen(true),
        itemsCount: data?.length || 0,
        selectedItemsCount: isMultiple ? props.values?.length || 0 : 1,
      })
    }

    return typeof label === 'string' ? (
      <View className="flex-row items-center gap-2">
        <Label className="font-bold text-lg text-foreground">{label}</Label>
      </View>
    ) : (
      label
    )
  }, [label, renderItem, selectedItem, setOpen, data?.length, isMultiple, props.values?.length])

  return (
    <>
      <Pressable
        role="button"
        key={selectedItem ? value : 'trigger'}
        disabled={disabled}
        onPress={() => !disabled && setOpen(true)}
        className={cn(
          'native:h-12 disabled:scale-100! disabled:bg-background! relative flex w-full flex-row items-center rounded-xl border-2 border-muted bg-background px-4 py-4 text-foreground transition-all duration-150 active:scale-[0.98]',
          !!selectedItem && 'border-accent p-0',
          disabled && 'pointer-events-none opacity-50',
          className
        )}>
        {triggerContent}
        <Icon
          name="chevron-down"
          size={selectedItem || (isMultiple && (props.values?.length || 0) > 0) ? 24 : 18}
          className={cn(
            'ml-auto shrink-0 text-foreground opacity-70',
            (selectedItem || (isMultiple && (props.values?.length || 0) > 0)) && 'mr-4'
          )}
        />
      </Pressable>

      <BottomSheet
        modalKey={modalKey}
        isOpen={isOpen}
        onOpenChange={setOpen}
        showHandle={false}
        className={cn('relative px-3 pt-6', sheetClassName)}
        showCloseButton
        stackBehavior={stackBehavior}>
        {!!sheetTitle && <SectionTitle className="px-4 pb-0">{sheetTitle}</SectionTitle>}

        {isLoading && <LoadingView />}

        <AnimatedLegendList
          key={`${modalKey}-list`}
          {...scrollProps}
          {...listProps}
          {...scrollViewFooterProps}
          className={cn('max-h-[70vh] min-h-[200px] flex-1 pb-6 pt-4', listClassName)}
          data={data || []}
          keyExtractor={(item) => keyExtractor(item as T)}
          extraData={extraData}
          getItemType={getItemType}
          onEndReached={onEndReached}
          onEndReachedThreshold={onEndReachedThreshold ?? 0.5}
          renderItem={({ item, extraData: listExtra }) => {
            const typedItem = item as T
            const itemKey = keyExtractor(typedItem)

            const selectedKeys = (
              listExtra as unknown as {
                selectedKeys?: string[]
              }
            )?.selectedKeys

            const isSelected = selectedKeys
              ? selectedKeys.includes(itemKey)
              : selectedKey === itemKey

            return renderItem({
              item: typedItem,
              selected: isSelected,
              context: 'option',
              onSelect: () => {
                if (isMultiple) {
                  const current = props.values || []
                  const exists = current.includes(itemKey)
                  const next = exists ? current.filter((k) => k !== itemKey) : [...current, itemKey]

                  props.onChangeValues?.(next)
                } else {
                  onChange?.(typedItem)
                  setOpen(false)
                }
              },
              itemsCount: data?.length || 0,
              selectedItemsCount: isMultiple ? selectedKeys?.length || 0 : 1,
            })
          }}
          ListFooterComponent={
            footer || isFetchingNextPage ? (
              <View style={{ height: footerHeight }}>{isFetchingNextPage && <LoadingView />}</View>
            ) : undefined
          }
        />

        {footerElement}
        {scrollIndicator}
      </BottomSheet>
    </>
  )
}
